import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue';
import { MonetizationType, ObjectType, Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
	type GetBuyBoxOffersQuery,
} from '@/components/buybox-new/graphql/GetBuyBoxOffers.query';
import { formattedReleaseDate, timestamp } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/user.store';
import { ModalHelper } from '@/helpers/modal-helper';
import { EXCLUDED_PACKAGES } from '@/constants/buybox-excluded-packages.constant';
import RegularBuyBoxNew from '@/components/buybox-new/RegularBuyBoxNew.vue';
import BuyBoxCountrySwitcher from '@/components/buybox-new/BuyBoxCountrySwitcher.vue';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore, useUserStore, useConstantStore } from '@/helpers/composables/useStores';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';

import { AQTVTitleDetailPlacementsVariants } from '@/components/experiments/AQTVTitleDetailPlacememts';

import PromotedBuyBoxNew from '@/components/buybox-new/PromotedBuyBoxNew.vue';
import BuyBoxFilterNew from '@/components/buybox-new/BuyBoxFilterNew.vue';
import BuyBoxTypeSwitcher from '@/components/buybox-new/BuyBoxTypeSwitcher.vue';
import { UpcomingRelease, UpcomingReleaseBuybox } from '@/components/buybox-new/BuyBoxUpcomingNew.vue';
import BuyBoxUpcomingWithNoOffers from '@/components/buybox-new/BuyBoxUpcomingWithNoOffers.vue';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { allowedLanguagesUpcomingReleases } from '@/components/buybox/utils/upcoming-helpers';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxNew',
  props: {
    title: null,
    titleId: null,
    titleName: null,
    titleObjectId: null,
    titleObjectType: null,
    originalReleaseYear: null,
    isTitleUpcoming: { type: Boolean },
    sponsoredAd: { default: undefined },
    isTitleInWatchlist: { type: Boolean, default: true },
    buyBoxSwitchType: null,
    hideBuyBoxSwitcher: { type: Boolean, default: false },
    isAVODActive: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    seasonNumber: null,
    episodeNumber: null,
    appleQualityTvExpVariant: { default: null }
  },
  emits: {
	onWatchlistToggle: (createdAt: string | null) => ({ createdAt }),
},
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, titleId: string, titleName?: string, titleObjectId: number, titleObjectType: ObjectType, originalReleaseYear?: number, isTitleUpcoming: boolean, sponsoredAd: SponsoredAdFragment | undefined, isTitleInWatchlist: boolean, buyBoxSwitchType?: NewBuyBoxView, hideBuyBoxSwitcher: boolean, isAVODActive: boolean, additionalContexts: SnowplowContext[], seasonNumber?: number | null, episodeNumber?: number | null, appleQualityTvExpVariant: AQTVTitleDetailPlacementsVariants | null };

const OfferReporter = () => import('@/components/buybox/OfferReporter.vue');
const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');
// need to import this immediately because it's above the fold and could cause CLS
const buyboxContainer = ref<HTMLElement | null>(null);




const { setInWatchlist, onIsLoggedInChange } = useTitleWatchlist(props.sponsoredAd);
const { setNewBuyboxFilterType, newBuyboxFilterType, isLoggedIn } = useUserStore();
const { country, language } = useLanguageStore();
const { titleProviderCount, allProvidersByShortName } = useConstantStore();
const { t } = useI18n();

const queryVariables = computed<GetBuyBoxOffersQueryVariables>(() => ({
	nodeId: props.titleId,
	country: country.value,
	language: language.value,
	filterBuy: {
		monetizationTypes: [MonetizationType.Buy],
		bestOnly: true,
	},
	filterFlatrate: {
		monetizationTypes: [
			MonetizationType.Flatrate,
			MonetizationType.FlatrateAndBuy,
			MonetizationType.Ads,
			MonetizationType.Free,
			MonetizationType.Cinema,
		],
		bestOnly: true,
	},
	filterRent: {
		monetizationTypes: [MonetizationType.Rent],
		bestOnly: true,
	},
	filterFree: {
		monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
		bestOnly: true,
	},
}));

const { result, loading } = useQuery<GetBuyBoxOffersQuery, GetBuyBoxOffersQueryVariables>(
	GetBuyBoxOffersDocument,
	queryVariables
);

const offers = computed(() => {
	if (
		result.value?.node?.__typename === 'Episode' ||
		result.value?.node?.__typename === 'Movie' ||
		result.value?.node?.__typename === 'Season' ||
		result.value?.node?.__typename === 'Show'
	) {
		const { buy, rent, fast, free, flatrate, offerCount, maxOfferUpdatedAt, bundles } = result.value?.node;
		const res: any = {
			stream: [],
			rent,
			buy,
			free,
			fast,
			cinema: [],
			bundles,
			promotedOffers: [],
			offerCount,
			maxOfferUpdatedAt,
		};
		if (result.value?.node.__typename !== 'Episode') {
			res.promotedOffers = result.value?.node.promotedOffers;
		}
		flatrate.forEach(offer =>
			offer.monetizationType === MonetizationType.Cinema ? res.cinema.push(offer) : res.stream.push(offer)
		);

		return res;
	}

	return {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		bundles: [],
		promotedOffers: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};
});

const showPromotedBuyBox = computed(() => offers.value.promotedOffers?.length > 0);

const promotedBuyBoxOffers = computed(() => ({
	promotedOffers: offers.value?.promotedOffers ?? [],
}));

const filteredPromotedBuyBoxOffers = computed(() => {
	let _offers = promotedBuyBoxOffers.value;
	//temp hide AMZ titles on apple

	const { promotedOffers } = _offers;
	const filteredOffers = {
		promotedOffers: promotedOffers
			.filter((offer: Offer) => !EXCLUDED_PACKAGES.value.includes(offer.package.packageId))
			.filter((offer: Offer) => (hasApple.value ? offer.package.packageId !== 9 : true)),
	};
	_offers = filteredOffers;
	//temp hide AMZ titles on apple

	return _offers;
});

const buyBoxOffers = computed(() => ({
	offerCount: offers.value?.offerCount ?? 0,
	flatrate: offers.value.stream ?? [],
	free: offers.value?.free ?? [],
	fast: offers.value?.fast ?? [],
	rent: offers.value?.rent ?? [],
	buy: offers.value?.buy ?? [],
	cinema: offers.value?.cinema ?? [],
}));

const filteredBuyBoxOffers = computed(() => {
	let _offers = buyBoxOffers.value;

	const { offerCount, ...allOffers } = _offers;
	const filteredOffers = Object.fromEntries(
		Object.entries(allOffers).map(([key, value]) => [
			key,
			value.filter((offer: Offer) => !EXCLUDED_PACKAGES.value.includes(offer.package.packageId)),
		])
	) as typeof allOffers;
	_offers = {
		...filteredOffers,
		offerCount,
	};

	return _offers;
});

const buyBoxFilterOptions = computed(
	() =>
		(Object.keys(filteredBuyBoxOffers.value) as (keyof typeof filteredBuyBoxOffers.value)[]).filter(
			key => key !== 'offerCount' && filteredBuyBoxOffers.value[key]?.length > 0
		) as BuyBoxFilterOptions[]
);

const isEpisodeBuybox = computed(() => result.value?.node?.__typename === 'Episode');

// episode buybox filter is not currently being stored. keep state for each Episode buybox at buybox level
const episodeBuyBoxType = ref(isEpisodeBuybox.value ? BuyBoxFilterOptions.ALL : null);

const buyboxFilter = computed(() => (isEpisodeBuybox.value ? episodeBuyBoxType.value : newBuyboxFilterType.value));

function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
	isEpisodeBuybox.value ? (episodeBuyBoxType.value = payload.value) : setNewBuyboxFilterType(payload.value);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'buybox',
			property: payload.value,
			nonInteraction: payload.nonInteraction,
		},
		props.additionalContexts
	);

	// trackTitleDetailRedesignNewBuyBoxExp({
	// 	action: 'click',
	// 	label: `filter_toggled_${payload.value}`,
	// });
}

const lastUpdated = computed(() => offers.value.maxOfferUpdatedAt);

const offersProviderShortNames = computed<string[]>(() => {
	const { offerCount, ...others } = filteredBuyBoxOffers.value;
	return Object.values(others)
		.flat()
		.map(offer => offer.package.shortName);
});

const upcomingReleases = computed(() => {
	if (!allowedLanguagesUpcomingReleases.includes(language.value)) return [];

	return props.title.content.upcomingReleases.filter(release => {
		return (
			!!release.package &&
			!!allProvidersByShortName.value[release.package.shortName] &&
			!offersProviderShortNames.value.includes(release.package.shortName)
		);
	});
});

const upcomingReleasesGroupedByDateAndType = computed(() => {
	//group upcoming releases by formatedReleaseDate (date, day, month, season etc..) and by releaseType (theatrical, digital)
	return upcomingReleases.value?.reduce((acc, item, index) => {
		const releaseDate: string = item.releaseDate
			? formattedReleaseDate(item.releaseDate, item.label, language.value, 'short', country.value).heading
			: index.toString();
		const key = `${releaseDate}_${item.releaseType}`;

		if (!acc[key]) {
			acc[key] = [];
		}
		acc[key].push(item);
		return acc;
	}, {} as Record<string, UpcomingRelease[]>);
});

const upcomingReleasesFromGroups = computed(() => {
	return Object.values(upcomingReleasesGroupedByDateAndType.value).flatMap(group =>
		group.map(
			(item, idx) =>
				({
					offer: item,
					isInGroup: group.length > 1,
					isLastOfGroup: group.length > 1 && idx === group.length - 1,
					isFirst: idx === 0,
				} as UpcomingReleaseBuybox)
		)
	);
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

//temp hide AMZ titles on apple

const allOffers = computed<TitleOfferFragment[]>(() => {
	return filteredBuyBoxOffers.value?.free.concat(filteredBuyBoxOffers.value?.flatrate) ?? [];
});

const hasApple = computed(() => allOffers.value.some(offer => offer.package.packageId === 350));
//temp hide AMZ titles on apple

async function onNotifyMe() {
	if (props.title && props.titleId) {
		const updatedTitle = await setInWatchlist(
			props.titleId,
			props.isTitleInWatchlist,
			{
				title: props.title.content.title,
				objectType: props.title.objectType,
				objectId: props.title.objectId,
			},
			{
				source: 'buybox',
			}
		);

		if (updatedTitle) {
			emits('onWatchlistToggle', updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt);
		}
	}
}
watch(
	isLoggedIn,
	newValue => {
		if (!newValue) return;
		if (props.title && props.titleId) {
			onIsLoggedInChange(
				props.titleId || '',
				{
					title: props.title?.content.title,
					objectType: props.title?.objectType,
					objectId: props.title?.objectId,
				},
				props.isTitleInWatchlist,
				'buybox'
			);
		}
	},
	{ immediate: true }
);

function watchNowTitleClicked() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'click', label: 'watch_now_headline_clicked' },
		props.additionalContexts
	);
	// trackTitleDetailRedesignNewBuyBoxExp({
	// 	action: 'click',
	// 	label: 'watch_now_headline_clicked',
	// });
	if (!props.isAVODActive) {
		const element = buyboxContainer.value?.querySelector('#regular-buybox');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} else {
		ModalHelper.openModal(
			PlexPlayerModal,
			{ offer: props.title?.plexPlayerOffers[0], title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}
}

return { __sfc: true,OfferReporter, PlexPlayerModal, buyboxContainer, emits, props, setInWatchlist, onIsLoggedInChange, setNewBuyboxFilterType, newBuyboxFilterType, isLoggedIn, country, language, titleProviderCount, allProvidersByShortName, t, queryVariables, result, loading, offers, showPromotedBuyBox, promotedBuyBoxOffers, filteredPromotedBuyBoxOffers, buyBoxOffers, filteredBuyBoxOffers, buyBoxFilterOptions, isEpisodeBuybox, episodeBuyBoxType, buyboxFilter, handleFilterClick, lastUpdated, offersProviderShortNames, upcomingReleases, upcomingReleasesGroupedByDateAndType, upcomingReleasesFromGroups, timestampText, allOffers, hasApple, onNotifyMe, watchNowTitleClicked, RegularBuyBoxNew, BuyBoxCountrySwitcher, PromotedBuyBoxNew, BuyBoxFilterNew, BuyBoxTypeSwitcher, BuyBoxUpcomingWithNoOffers }
}

})